import { Button, Input, Modal, Form, Row, Col, Card, Typography } from "antd";
import axios from "axios";
import React, { useContext, useEffect, useState } from "react";
import { isAuthenticated } from "../../../commons/Auth/auth";
import MainButtonComp from "../../../commons/Buttons/MainButtonComp";
import { Success } from "../../../commons/Messages/Messages";
import { SocketContext } from "../../../context/SocketContext";
import { generalErrorAlert } from "../../../commons/GeneralErrorAlert";
import { useTranslation } from "react-i18next";

const { Text } = Typography;

const UpdateBankDetails = ({ data, updateParent }) => {
  const { t } = useTranslation("dashboard");

  const socket = useContext(SocketContext);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const showModal = () => {
    setIsModalOpen(true);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  const onSubmit = async values => {
    setLoading(true);
    try {
      await axios.put(`${process.env.REACT_APP_BACKEND_URL}/users/me/bank-details`, values, {
        headers: { Authorization: "Bearer " + localStorage.getItem("token") }
      });
      Success(t("Bank details updated successfully!"));
      handleCancel();
      updateParent();
      socket?.emit("refresh service provider data", {
        user: isAuthenticated()?.serviceProviderId
      });
    } catch (err) {
      generalErrorAlert(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isModalOpen) {
      form.setFieldsValue({ ...data, iban: data?.iban?.toUpperCase().replace(/\s+/g, "") });
    }
  }, [data, form, isModalOpen]);

  const show = value => value || <Text type="secondary">{t("Not provided")}</Text>;

  return (
    <Card
      title={t("Bank Details")}
      extra={<MainButtonComp text="Edit" editBtn onClick={showModal} />}
    >
      <Text type="secondary" className="d-block mb-3">
        {t("These details are printed on your invoices.")}
      </Text>
      <Row gutter={[16, 12]}>
        <Col xs={24} md={12}>
          <Text strong>{t("Bank Name")}:</Text> {show(data?.bankName)}
        </Col>
        <Col xs={24} md={12}>
          <Text strong>{t("Country")}:</Text> {show(data?.country)}
        </Col>
        <Col xs={24} md={12}>
          <Text strong>{t("City")}:</Text> {show(data?.city)}
        </Col>
        <Col xs={24} md={12}>
          <Text strong>{t("Address")}:</Text> {show(data?.address)}
        </Col>
        <Col xs={24} md={12}>
          <Text strong>{t("IBAN")}:</Text> {show(data?.iban)}
        </Col>
        <Col xs={24} md={12}>
          <Text strong>{t("SWIFT Code")}:</Text> {show(data?.swiftCode)}
        </Col>
      </Row>
      <Modal
        title={t("Update Bank Details")}
        footer={
          <Button type="primary" loading={loading} block onClick={() => form.submit()}>
            {t("Save")}
          </Button>
        }
        forceRender
        open={isModalOpen}
        onCancel={handleCancel}
      >
        <Form form={form} layout="vertical" onFinish={onSubmit}>
          <Form.Item
            name="bankName"
            label={t("Bank Name")}
            rules={[{ required: true, message: t("enter", { field: t("Bank Name") }) }]}
          >
            <Input autoComplete="off" />
          </Form.Item>
          <Form.Item
            name="iban"
            label={t("IBAN")}
            normalize={value => value?.toUpperCase().replace(/\s+/g, "")}
            rules={[
              { required: true, message: t("enter", { field: t("IBAN") }) },
              {
                pattern: /^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/,
                message: t("Please enter a valid IBAN.")
              }
            ]}
          >
            <Input autoCapitalize="characters" autoComplete="off" spellCheck={false} />
          </Form.Item>
          <Form.Item name="address" label={t("Address")}>
            <Input />
          </Form.Item>
          <Form.Item name="city" label={t("City")}>
            <Input autoComplete="address-level2" />
          </Form.Item>
          <Form.Item name="country" label={t("Country")}>
            <Input autoComplete="country-name" />
          </Form.Item>
          <Form.Item
            name="swiftCode"
            label={t("SWIFT Code")}
            normalize={value => value?.toUpperCase().replace(/\s+/g, "")}
          >
            <Input autoCapitalize="characters" autoComplete="off" spellCheck={false} />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default UpdateBankDetails;
